/**
 * Static fallback when Firestore `roles/` is empty or unreachable.
 * Primary source: `rolesService.loadPublishedRoles()`.
 */

export const ROADMAP_LEARN_ROLES_FALLBACK = [
  {
    id: 'programmer',
    roleId: 'programmer',
    title: 'Programmer',
    promise: 'Core programming skills across languages',
    iconClass: 'ti-code',
    includesOrphanTracks: true,
    order: 0,
    languages: [
      'python',
      'javascript',
      'java',
      'typescript',
      'cpp',
      'swift',
      'c',
      'go',
      'kotlin',
      'php',
      'ruby',
      'rust'
    ],
    tracks: [
      { entryId: 'entry-programmer-python-pf', trackId: 'python-programming-fundamentals', order: 0 },
      {
        entryId: 'entry-programmer-javascript-pf',
        trackId: 'javascript-programming-fundamentals',
        order: 1
      },
      { entryId: 'entry-programmer-java-pf', trackId: 'java-programming-fundamentals', order: 2 },
      {
        entryId: 'entry-programmer-typescript-pf',
        trackId: 'typescript-programming-fundamentals',
        order: 3
      },
      { entryId: 'entry-programmer-cpp-pf', trackId: 'cpp-programming-fundamentals', order: 4 },
      { entryId: 'entry-programmer-swift-pf', trackId: 'swift-programming-fundamentals', order: 5 },
      { entryId: 'entry-programmer-c-pf', trackId: 'c-programming-fundamentals', order: 6 },
      { entryId: 'entry-programmer-go-pf', trackId: 'go-programming-fundamentals', order: 7 },
      { entryId: 'entry-programmer-kotlin-pf', trackId: 'kotlin-programming-fundamentals', order: 8 },
      { entryId: 'entry-programmer-php-pf', trackId: 'php-programming-fundamentals', order: 9 },
      { entryId: 'entry-programmer-ruby-pf', trackId: 'ruby-programming-fundamentals', order: 10 },
      { entryId: 'entry-programmer-rust-pf', trackId: 'rust-programming-fundamentals', order: 11 }
    ]
  },
  {
    id: 'backend-developer',
    title: 'Backend Developer',
    promise: 'APIs, databases, and server-side logic with Java & Spring Boot',
    iconClass: 'ti-server',
    languages: ['java']
  },
  {
    id: 'frontend-developer',
    title: 'Frontend Developer',
    promise: 'Interactive UIs with JavaScript, TypeScript, and modern frameworks',
    iconClass: 'ti-layout-grid',
    languages: ['javascript', 'typescript']
  },
  {
    id: 'data-scientist',
    title: 'Data Scientist',
    promise: 'Analyze data, build ML models, and work with deep learning',
    iconClass: 'ti-chart-dots',
    languages: ['python']
  },
  {
    id: 'android-developer',
    title: 'Android Developer',
    promise: 'Build native Android apps with Java',
    iconClass: 'ti-device-mobile',
    languages: ['java']
  },
  {
    id: 'security-engineer',
    title: 'Security Engineer',
    promise: 'Secure applications, systems, and data',
    iconClass: 'ti-shield-lock',
    languages: ['java']
  },
  {
    id: 'data-engineer',
    title: 'Data Engineer',
    promise: 'Design pipelines and enterprise data systems',
    iconClass: 'ti-database',
    languages: ['java']
  },
  {
    id: 'interview-prep',
    title: 'Interview Prep',
    promise: 'DSA and coding interview practice across languages',
    iconClass: 'ti-briefcase',
    languages: ['java', 'javascript', 'python', 'typescript']
  }
];

/** @deprecated Use ROADMAP_LEARN_ROLES_FALLBACK — kept for imports during migration */
export const ROADMAP_LEARN_ROLES = ROADMAP_LEARN_ROLES_FALLBACK;

/** Default role when opening the Learn hub Role tab with no `?roleId=`. */
export const DEFAULT_LEARN_ROLE_ID = 'programmer';

/**
 * @param {Array<{ roleId?: string, id?: string, order?: number }>|null|undefined} roles
 * @returns {string}
 */
export function defaultLearnRoleIdFromRoles(roles) {
  if (!roles?.length) return DEFAULT_LEARN_ROLE_ID;
  const programmer = roles.find((r) => (r.roleId || r.id) === DEFAULT_LEARN_ROLE_ID);
  if (programmer) return programmer.roleId || programmer.id;
  const sorted = [...roles].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return sorted[0]?.roleId || sorted[0]?.id || DEFAULT_LEARN_ROLE_ID;
}

/**
 * @param {Array<{ id?: string, roleId?: string }>|null|undefined} roles
 * @param {string|null|undefined} roleId
 */
export function findLearnRole(roles, roleId) {
  if (!roleId || !roles?.length) return null;
  const id = String(roleId).trim();
  return roles.find((r) => r.id === id || r.roleId === id) ?? null;
}

/** @param {string|null|undefined} roleId */
export function findRoadmapLearnRole(roleId, roles = ROADMAP_LEARN_ROLES_FALLBACK) {
  if (!roleId) return null;
  return findLearnRole(roles, roleId);
}
